<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    import { createEventDispatcher } from 'svelte';
    import { generateElementId } from '@oat-sa-private/ui-core';
    import SelectableChoice from './SelectableChoice.svelte';

    const dispatch = createEventDispatcher();

    const orientations = {
        vertical: 'vertical',
        horizontal: 'horizontal'
    };

    /**
     * SelectableChoices component displays a list of choices
     * @property {Object[]} choices - choices array
     * @property {String[]} [selected=[]] - selected choices array
     * @property {String[]} [eliminated=[]] - choice index in the list
     * @property {String[]} [masked=[]] - list of masked choices keys
     * @property {String} [orientation="horizontal"] - list orientation `horizontal | `vertical``
     * @property {Number} [stacking=0] - stacking number of columns in horizontal orientation
     * @property {Number} [maxChoices=-1] - if 1 radio button will be displayed as a control
     * @property {Boolean} [controls=true] - controls display of choice controls
     * @property {Boolean} [disabled=false] - disabled state of list
     * @property {Boolean|String} [choiceLabel=false] - display a label in front of the choice `decimal` | `lowerAlpha` | `upperAlpha`
     * @property {String} [choiceLabelSuffix=''] - add a suffix to the choice label like `)` or `.`
     * @property {String} [name] - `name` of input element
     * @property {Boolean} [showCaption=true] - controls display of the choices caption container
     * @property {Boolean} [eliminable=false] - controls display of choices elimination button
     * @property {Boolean} [maskable=false] - controls display of choices masked button
     * @fires 'change'
     */
    export let choices;
    export let selected = [];
    export let eliminated = [];
    export let masked = [];
    export let orientation = orientations.horizontal;
    export let stacking = 0;
    export let maxChoices = -1;
    export let controls = true;
    export let disabled = false;
    export let choiceLabel = false;
    export let choiceLabelSuffix = '';
    export let name = generateElementId('selectablechoice');
    export let showCaption = true;
    export let eliminable = false;
    export let maskable = false;

    /**
     * Handle choice changes
     * NOTE: only detects changes from SelectableChoice children created through 'choices' prop, not slotted choices
     * @param {Event} event
     * @fires change with current 'selected' and 'eliminated' values in event.detail
     */
    function handleChange(event) {
        dispatch('change', { selected, eliminated, masked, event: event.detail });
        event.stopPropagation();
    }
</script>

<style>
    .selectable-choice-container {
        max-width: 100%;
    }
    ul {
        list-style-type: none;
        display: flex;
        margin: calc(-1 * var(--space-1x)); /* remove margin around content */
        padding: 0;

        /* direction and stacking */
        &.vertical {
            flex-flow: column nowrap;
            align-items: stretch;
            & :global(li) {
                flex: 1 0 100%;
            }
        }
        &.horizontal {
            flex-flow: row wrap;
        }
    }
</style>

<div class="selectable-choice-container" role="group" aria-roledescription="selectablechoice">
    <ul class={orientation}>
        {#if choices}
            {#each choices as choice, index}
                <SelectableChoice
                    {...choice}
                    {name}
                    {index}
                    bind:selected
                    bind:eliminated
                    bind:masked
                    {showCaption}
                    {eliminable}
                    {maskable}
                    {choiceLabel}
                    {choiceLabelSuffix}
                    {maxChoices}
                    {controls}
                    {disabled}
                    {stacking}
                    on:click
                    on:keypress
                    on:change={handleChange}>
                    {choice.label}
                </SelectableChoice>
            {/each}
        {:else}
            <slot />
        {/if}
    </ul>
</div>
